import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    checkIsPaidSiteProductKey,
    getSiteProductDefinition,
    SitePlanTarget,
} from '@gitbook/app-pricing';
import { Actions, Badge, Button, Card, Description, Heading, toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { getTrialEndingNotice, SiteTypeCardFeaturesList } from '../../../../../Components';
import { useNavigationState } from '../../../../../navigation';
import {
    apiClient,
    type OrgTrialDetails,
    type SiteCapability,
    useRedirectToStripe,
} from '../../../../../state';
import {
    ChangeSitePlanDialogLayout,
    useChangeSitePlanDialog,
    useUpgradeStatusModal,
} from '../../Plan';

export function SitePlan(props: {
    trial: OrgTrialDetails;
    site: api.Site;
    organization: api.Organization;
    sitePermissions: SiteCapability;
}) {
    const { trial, organization, site, sitePermissions } = props;

    const { openChangeSitePlanDialog } = useChangeSitePlanDialog();
    const hasOngoingOrExpiredTrial =
        trial.status === api.BillingTrialStatus.Active ||
        trial.status === api.BillingTrialStatus.Expired;

    const definition = getSiteProductDefinition(site.type);
    const isPaidPlan = checkIsPaidSiteProductKey(site.type);

    const updateSitePlanOrCheckout = useUpdateSitePlanOrCheckoutCallback(organization);

    return (
        <Card>
            <div className="flex items-center gap-2 border-b p-4 max-md:flex-col">
                <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                        <Heading className="flex items-center gap-2">
                            <span>{definition.title} plan</span>
                            {isPaidPlan && hasOngoingOrExpiredTrial ? (
                                <Badge
                                    kind="upgrade"
                                    label={getTrialEndingNotice({
                                        daysLeft: trial.daysLeft,
                                        prefix: 'GitBook',
                                    })}
                                />
                            ) : null}
                        </Heading>
                    </div>
                    <Description>{definition.description}</Description>
                </div>
                <Actions>
                    {hasOngoingOrExpiredTrial ? (
                        <UpgradeButton
                            trial={trial}
                            organization={organization}
                            sitePermissions={sitePermissions}
                        />
                    ) : (
                        <Button
                            kind="secondary"
                            label="Change site plan"
                            disabled={!sitePermissions.canAdminSite}
                            onPress={() =>
                                openChangeSitePlanDialog({
                                    site,
                                    organization,
                                    layout: ChangeSitePlanDialogLayout.Branded,
                                    reason: 'settings',
                                    onChangeSitePlan: updateSitePlanOrCheckout,
                                })
                            }
                            tooltip={
                                sitePermissions.canAdminSite
                                    ? undefined
                                    : {
                                          tooltip:
                                              'You don’t have permission to change the site plan',
                                      }
                            }
                        />
                    )}
                </Actions>
            </div>
            <div className="p-4">
                <SiteTypeCardFeaturesList
                    siteType={site.type}
                    target={SitePlanTarget.CUSTOMERS}
                    columns={2}
                />
            </div>
        </Card>
    );
}

function UpgradeButton(props: {
    trial: OrgTrialDetails;
    organization: api.Organization;
    sitePermissions: SiteCapability;
}) {
    const { trial, organization, sitePermissions } = props;
    const { screen: currentScreen } = useNavigationState();

    return (
        <Button
            kind="upgrade"
            label="Upgrade"
            disabled={!sitePermissions.canAdminSite}
            href={{
                overlay: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab: {
                        type: 'billing',
                        returnScreen: currentScreen,
                    },
                    hash: 'site_settings_plan',
                },
            }}
            onPress={() => {
                analytics.logEvent(
                    analytics.createOrgEvent(organization.id, {
                        type: 'organization_open_upgrade',
                        currentFeaturesSet: organization.plan,
                        reason: 'site_settings_plan',
                    })
                );
            }}
            tooltip={{
                tooltip:
                    trial.status === api.BillingTrialStatus.Active
                        ? 'Upgrade at any time to continue accessing paid features.'
                        : 'Subscribe today to keep all the features you’ve tried.',
            }}
        />
    );
}

export function useUpdateSitePlanOrCheckoutCallback(organization: api.Organization) {
    const redirectToStripe = useRedirectToStripe();
    const { openUpgradeStatusModal } = useUpgradeStatusModal();

    return async (params: {
        site: api.Site;
        selectedSitePlan: api.SiteType;
        reason: api.SiteUpgradeReason;
        closeDialog: () => void;
    }) => {
        const { site, selectedSitePlan, reason, closeDialog } = params;
        const selectedPlanDefinition = getSiteProductDefinition(selectedSitePlan);

        const { data: response } = await apiClient.sites.updateSitePlan(organization.id, site.id, {
            plan: selectedSitePlan,
            reason,
        });

        switch (response.type) {
            case 'checkout':
                closeDialog();
                await redirectToStripe(response.sessionId);
                break;
            case 'success':
                switch (response.outcome) {
                    case 'no-payment-changes':
                        toast.success(`Your site was switched to ${selectedPlanDefinition.title}.`);
                        break;
                    case 'upgraded':
                    case 'downgraded':
                        openUpgradeStatusModal();
                        break;
                }

                closeDialog();
                break;
            default:
                assertNever(response);
        }
    };
}
